import * as THREE from 'three';
import type { PeerInfo } from '../../server/protocol';
import type { Cursor } from './collab';

/** Größe des Cursor-Markers in Weltmetern. */
const MARKER_RADIUS = 0.02;

interface PresenceItem {
  marker: THREE.Mesh;
  stem: THREE.Line;
  label: HTMLDivElement;
  cursor: Cursor;
}

/**
 * Zeigt die Cursor der anderen Teilnehmer in der Szene: farbiger Punkt am
 * Hover-Punkt, Stift-Linie zum Boden und ein Namensschild (HTML-Layer über dem
 * Canvas, pro Frame an die Projektion angepasst).
 */
export class PresenceRenderer {
  readonly group = new THREE.Group();
  private layer: HTMLDivElement;
  private items = new Map<string, PresenceItem>();
  private readonly markerGeometry = new THREE.SphereGeometry(MARKER_RADIUS, 12, 8);
  private selfId: string | null = null;

  constructor(container: HTMLElement) {
    this.layer = document.createElement('div');
    this.layer.className = 'presence-layer';
    container.appendChild(this.layer);
    this.group.renderOrder = 20;
    this.group.visible = false;
  }

  /** Teilnehmerstand mit der Kollaboration abgleichen (hinzufügen/entfernen/umbenennen). */
  setPeers(peers: PeerInfo[], cursors: Map<string, Cursor>): void {
    const alive = new Set(peers.map((p) => p.clientId));
    for (const id of [...this.items.keys()]) {
      if (!alive.has(id)) this.disposeItem(id);
    }
    for (const peer of peers) {
      if (peer.clientId === this.selfId) continue;
      let item = this.items.get(peer.clientId);
      if (!item) {
        item = this.createItem(peer);
        this.items.set(peer.clientId, item);
      }
      item.label.textContent = peer.name;
      item.cursor = cursors.get(peer.clientId) ?? null;
    }
    this.group.visible = this.items.size > 0;
    if (!this.group.visible) this.layer.replaceChildren();
  }

  /** Eigene Client-ID setzen (der eigene Cursor wird nicht gezeichnet). */
  setSelf(clientId: string | null): void {
    this.selfId = clientId;
  }

  /** Pro Frame: Marker positionieren, Label an die Bildschirmprojektion heften. */
  render(camera: THREE.PerspectiveCamera, canvas: HTMLCanvasElement): void {
    if (this.items.size === 0) return;
    const v = new THREE.Vector3();
    for (const item of this.items.values()) {
      const visible = item.cursor !== null;
      item.marker.visible = visible;
      item.stem.visible = visible;
      item.label.style.display = visible ? '' : 'none';
      if (!item.cursor || !visible) continue;
      const [x, y, z] = item.cursor;
      item.marker.position.set(x, y, z);
      const pos = item.stem.geometry.getAttribute('position') as THREE.BufferAttribute;
      pos.setXYZ(0, x, y, z);
      pos.setXYZ(1, x, y, 0);
      pos.needsUpdate = true;

      v.set(x, y, z).project(camera);
      const behind = v.z > 1;
      item.label.style.display = behind ? 'none' : '';
      if (!behind) {
        item.label.style.left = `${canvas.offsetLeft + ((v.x + 1) / 2) * canvas.clientWidth}px`;
        item.label.style.top = `${canvas.offsetTop + ((1 - v.y) / 2) * canvas.clientHeight}px`;
      }
    }
  }

  clear(): void {
    for (const id of [...this.items.keys()]) this.disposeItem(id);
    this.group.visible = false;
  }

  dispose(): void {
    this.clear();
    this.markerGeometry.dispose();
    this.layer.remove();
  }

  // ---------- Internas ----------

  private createItem(peer: PeerInfo): PresenceItem {
    const material = new THREE.MeshBasicMaterial({ color: peer.color, depthTest: false, transparent: true });
    const marker = new THREE.Mesh(this.markerGeometry, material);
    marker.renderOrder = 20;

    const stemMaterial = new THREE.LineBasicMaterial({ color: peer.color, depthTest: false, transparent: true, opacity: 0.6 });
    const stem = new THREE.Line(new THREE.BufferGeometry(), stemMaterial);
    stem.geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(6), 3));
    stem.renderOrder = 19;
    this.group.add(marker, stem);

    const label = document.createElement('div');
    label.className = 'presence-label';
    label.style.setProperty('--peer-color', peer.color);
    label.textContent = peer.name;
    this.layer.appendChild(label);

    return { marker, stem, label, cursor: null };
  }

  private disposeItem(id: string): void {
    const item = this.items.get(id);
    if (!item) return;
    this.group.remove(item.marker, item.stem);
    (item.marker.material as THREE.Material).dispose();
    (item.stem.material as THREE.Material).dispose();
    item.stem.geometry.dispose();
    item.label.remove();
    this.items.delete(id);
  }
}
